import { PAGINATION_DEFAULT_LIMIT } from 'app.constants'
import { useNavigate } from 'react-router-dom'
import { Location } from 'types'
import {
    getUserId,
    getShopId,
    getStorageLocationId, queryClient,
    updateLocationId,
    useMutationHandler,
    usePaginationHandler,
    useQueryHandler, isLoggedIn
} from 'utils'

const locationQuery = `

    id
    name
    phone
    email
    description
    image {
        id
        path
    }

    timezone
    
    businessAddress {
        country
        address
        address2
        city
        state
        zipCode
        latitude
        longitude
    }
    
    isDefault
    status
    
`

//useLocations:
export const useLocations = (searchTerm: string | null, limit: number = PAGINATION_DEFAULT_LIMIT, status?: boolean) => {
    const shopId = getShopId()
    return usePaginationHandler<Location[]>({
        query: `
            query getLocations(
                $shop: String!,
                $status: Boolean,
                $searchTerm: String,
                $limit: Int!,
                $after: String,
                $before: String
            ){
                getLocations(
                    shop: $shop,
                    status: $status,
                    searchTerm: $searchTerm,
                    limit: $limit,
                    after: $after,
                    before: $before
                ){
                    data{
                        ${locationQuery}
                    }
                    totalItems
                    remainingItems
                    nextCursor
                    previousCursor
                }
            }
        `,
        variables: {
            shop: shopId,
            status,
            searchTerm,
            limit,
            after: null,
            before: null
        },
        cacheKey: ['locations', shopId, searchTerm, status],
        limit,
        getResponse: json => json.data.getLocations,
        onSuccess: response => response.data
    })
}

//useLocation:
export const useLocation = (id: string, enabled?: boolean, onNotFound?: (json: any) => void) => {
    const shopId = getShopId()
    return useQueryHandler<Location>({
        query: `
            query getLocation(
                $shop: String!,
                $locationId: String!
            ){
                getLocation(
                    shop: $shop,
                    locationId: $locationId
                ){
                    ${locationQuery}
                }
            }
        `,
        variables: {
            shop: shopId,
            locationId: id
        },
        cacheKey: ['location', id, shopId],
        enabled,
        onSuccess: json => json.data.getLocation,
        onNotFound
    })
}

export const useCurrentLocation = () => {

    const shopId = getShopId()
    const locationId = getStorageLocationId()
    const hasSpecificLocation = locationId !== null && locationId !== 'all'
    const navigate = useNavigate()

    const removeLocation = () => {
        if(hasSpecificLocation){
            updateLocationId(null)
            queryClient.removeQueries(['currentLocation'])
            navigate(location.pathname)
        }
    }
    
    //handler:
    return useQueryHandler<Location | null>({
        query: hasSpecificLocation ? `
            query getLocation(
                $shop: String!,
                $locationId: String!
            ){
                getLocation(
                    shop: $shop,
                    locationId: $locationId
                ){
                    ${locationQuery}
                }
            }
        ` : `
            query getLocations(
                $shop: String!,
                $limit: Int!
            ){
                getLocations(
                    shop: $shop,
                    limit: $limit
                ){
                    data{
                        ${locationQuery}
                    }
                }
            }
        `,
        variables: hasSpecificLocation ? {
            shop: shopId,
            locationId
        } : {
            shop: shopId,
            limit: 1
        },
        cacheKey: [
            'currentLocation',
            hasSpecificLocation ? locationId : null,
            shopId,
            getUserId()
        ],
        enabled: isLoggedIn(),
        onSuccess: json => {
            let _location = null
            if(hasSpecificLocation){
                _location = json.data.getLocation
            }else if(locationId === null && json.data.getLocations.data.length > 0){
                _location = json.data.getLocations.data[0]
            }
            if(_location){
                updateLocationId(_location.id)
            }
            return _location
        },
        onNotFound: removeLocation,
        onForbidden: removeLocation,
        onUnknownError: removeLocation
    })

}

//create:
export const useCreateLocation = (location: Location) => {
    const shopId = getShopId()
    return useMutationHandler<null | boolean | 'name_is_used'>({
        query: `
            mutation createLocation(
                $shop: String!,
                $request: CreateLocationRequest!,
            ){
                createLocation(
                    shop: $shop,
                    request: $request
                ){
                    id
                }
            }
        `,
        variables: {
            shop: shopId,
            request: {

                name: location.name,
                phone: location.phone,
                email: location.email,
                description: location.description,
                image: location.image.id,

                timezone: location.timezone,

                businessAddress: location.businessAddress,

                isDefault: location.isDefault,
                status: location.status

            }

        },
        onSuccess: json => typeof json.data.createLocation.id === 'string',
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'name_is_used'
            }else{
                return null
            }
        }
    })

}

//update:
export const useUpdateLocation = (location: Location) => {
    const shopId = getShopId()
    return useMutationHandler<null | boolean | 'name_is_used'>({
        query: `
            mutation updateLocation(
                $shop: String!,
                $locationId: String!,
                $request: UpdateLocationRequest!,
            ){
                updateLocation(
                    shop: $shop,
                    locationId: $locationId,
                    request: $request
                ){
                    id
                }
            }
        `,
        variables: {
            shop: shopId,
            locationId: location.id,
            request: {

                name: location.name,
                phone: location.phone,
                email: location.email,
                description: location.description,
                image: location.image.id,

                timezone: location.timezone,

                businessAddress: location.businessAddress,

                isDefault: location.isDefault,
                status: location.status
                
            }

        },
        onSuccess: json => json.data.updateLocation.id === location.id,
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'name_is_used'
            }else{
                return null
            }
        }
    })
}
